/**
 * <nosto-react data-context="collection|search">
 *
 * Bridge web component that watches its descendant `#nosto-content` (or
 * `#nosto-search-content`) for the first injected child. As soon as
 * Nosto-rendered Preact content lands in the DOM, we flag the container
 * with `.ax-nosto-loaded`, which:
 *   - Reveals the Nosto content (display: block + fade-in)
 *   - Hides the skeleton placeholder (sibling selector in the section CSS)
 *   - Hides the native fallback (sibling selector in the section CSS)
 *
 * Fallback to the native rendering (`.show-native`) happens when:
 *   - The Nosto SDK initialised WITHOUT injecting the Search Templates
 *     loader (window.nostoTemplatesConfig absent: no production deployment,
 *     feature disabled, domain not whitelisted…). Detected ~1s after SDK
 *     ready instead of sitting on the skeleton for the full timeout.
 *   - Or nothing was injected within FALLBACK_TIMEOUT ms.
 *
 * After a fallback we keep watching for a while: if Nosto content arrives
 * late (slow network), we swap back to it so a slow load self-heals instead
 * of staying native.
 *
 * This script may be deferred since it doesn't define a custom element used
 * during initial HTML parsing — its job starts after Nosto's bundle runs.
 */

(function () {
  if (window.customElements && customElements.get("nosto-react")) return;

  const FALLBACK_TIMEOUT = 8000;
  const TEMPLATES_GRACE = 1000;
  const LATE_RECOVERY_WINDOW = 15000;
  const POLL_INTERVAL = 200;

  function findContent(root) {
    return (
      root.querySelector("#nosto-content") ||
      root.querySelector("#nosto-search-content")
    );
  }

  function findNative(root) {
    return (
      root.querySelector(".collection__native") ||
      root.querySelector(".search__native")
    );
  }

  function findSkeleton(root) {
    return (
      root.querySelector(".collection__skeleton") ||
      root.querySelector(".search__skeleton")
    );
  }

  /*
   * The three layers (Nosto, skeleton, native) are stacked in the same 1x1
   * grid cell so the crossfade never reflows — the cell sizes to the TALLEST
   * layer, which keeps the footer/pagination pinned while the swap plays.
   *
   * The catch: `#nosto-content` gets its `.ax-nosto-loaded` flag as soon as
   * its FIRST child lands, but at that moment the grid is still empty — the
   * <nosto-dynamic-card> elements fetch their `?view=card` markup one by one
   * over the next ~1.5s. If we drop the skeleton out of flow on a fixed timer
   * (the old 250ms `setTimeout`), the cell collapses to the half-empty Nosto
   * height, then GROWS card-by-card as each fetch resolves — which is exactly
   * the CLS the stacking was meant to prevent: the page first shrinks ("tout
   * disparaît"), then the pagination jumps to the top and walks back down as
   * rows fill in.
   *
   * So instead of a timer we keep the skeleton reserving space until the
   * Nosto grid has actually FILLED — i.e. it has rendered (close to) the
   * expected number of cards AND its height has stopped growing for two
   * consecutive frames. Only then do we collapse the inactive layers out of
   * flow. A hard cap (MAX_SETTLE_MS) guarantees we never sit on the skeleton
   * forever if the grid never reaches the expected count (e.g. last page with
   * fewer products, or a partial Nosto response).
   */
  const SWAP_TRANSITION_MS = 250;
  const SETTLE_POLL_MS = 100;
  const MAX_SETTLE_MS = 6000;

  function expectedCardCount(root) {
    const data = document.querySelector(".collection-nosto-data, .search-nosto-data");
    const n = data && parseInt(data.getAttribute("data-products-per-page"), 10);
    return n > 0 ? n : 0;
  }

  function countCards(content) {
    return content ? content.querySelectorAll("product-card, .product-card").length : 0;
  }

  // Reveal Nosto and drop the inactive layers, but only once `content` has
  // FILLED and stopped growing — so the shopper sees one clean crossfade from
  // skeleton to the finished grid, never the cards/pagination popping in.
  function collapseAfterSwap(elements, content) {
    const collapse = () => {
      // 1. Reveal: start the opacity crossfade now that the grid is complete.
      //    The skeleton was kept opaque on top during the whole fetch/fill, so
      //    nothing of Nosto's progressive rendering was ever visible.
      if (content) content.classList.add("ax-nosto-loaded");
      // 2. After the crossfade has played, pull the (now invisible) skeleton
      //    and native out of the flow. We're here only once the cell height is
      //    quiet, so the skeleton and the Nosto grid occupy the same height and
      //    removing the skeleton leaves the cell — and the footer below — put.
      setTimeout(() => {
        elements.forEach((el) => {
          if (el) el.style.display = "none";
        });
      }, SWAP_TRANSITION_MS);
    };

    if (!content) {
      // Fallback path (native takes over): nothing to reveal, just collapse the
      // skeleton after the transition.
      setTimeout(() => {
        elements.forEach((el) => { if (el) el.style.display = "none"; });
      }, SWAP_TRANSITION_MS);
      return;
    }

    // Watch the GRID CELL height, not just the Nosto content: the cell is
    // max(skeleton, nosto), so it stops changing only once BOTH the Nosto grid
    // has finished filling AND any transient skeleton/Nosto height mismatch
    // (subgrid reflow, toolbar chrome) has resolved. Collapsing only after the
    // cell is quiet means the skeleton leaves the flow at the exact height
    // Nosto already occupies — a true 0-CLS swap. A hard cap guarantees we
    // never sit on the skeleton forever on a partial/stalled response.
    const grid = content.closest("#collection") || content.parentElement || content;
    const expected = expectedCardCount(content);
    const started = Date.now();
    let lastHeight = -1;
    let lastCards = -1;
    let stableFrames = 0; // polls in a row with NO change to height OR card count

    const tick = () => {
      const height = Math.round(grid.getBoundingClientRect().height);
      const cards = countCards(content);
      const timedOut = Date.now() - started >= MAX_SETTLE_MS;

      // IMPORTANT: track card count, not just cell height. While the skeleton
      // is still in flow it pins the cell height, so `height` stays constant
      // even as Nosto streams cards in — keying "stable" off height alone would
      // declare victory mid-fill and yank the skeleton at e.g. 12/16 cards
      // (the cell then collapses and re-grows: the original CLS). The layout is
      // only truly quiet when BOTH the height and the card count stop moving.
      if (height === lastHeight && cards === lastCards) {
        stableFrames += 1;
      } else {
        stableFrames = 0;
        lastHeight = height;
        lastCards = cards;
      }

      // Two ways to be "settled":
      //  1. Full page: every expected card is present and nothing has changed
      //     for 3 polls (~300ms) — the common, fast path.
      //  2. Short page (collection smaller than products_per_page, or the last
      //     page): we'll never reach `expected`, so once at least one card has
      //     rendered and BOTH height and card count have been quiet for ~800ms
      //     we accept it. Because we now also require the card count to hold,
      //     this can't fire mid-stream while more cards are still arriving.
      const enoughCards = expected === 0 || cards >= expected;
      const settled =
        (enoughCards && stableFrames >= 3) ||
        (cards > 0 && stableFrames >= 8);

      if (settled || timedOut) {
        // collapse() reveals Nosto (crossfade) then removes the skeleton after
        // the transition — no outer delay, the grid is already complete.
        collapse();
        return;
      }
      setTimeout(tick, SETTLE_POLL_MS);
    };

    tick();
  }

  class NostoReact extends HTMLElement {
    connectedCallback() {
      const content = findContent(this);
      if (!content) return;

      this._started = Date.now();
      this._deadline = this._started + FALLBACK_TIMEOUT;
      this._fellBack = false;
      this._sdkHooked = false;
      this._poll = setInterval(() => this._tick(content), POLL_INTERVAL);

      this._tick(content);
    }

    // Fast fallback: once the SDK is ready, the include script has either
    // injected the templates loader (window.nostoTemplatesConfig set) or
    // skipped it for good — no point waiting the full timeout then. The
    // nostojs stub is defined by Nosto's async app embed, so it may not
    // exist yet when this element upgrades; we retry from the poll loop.
    _hookSdk() {
      if (this._sdkHooked || typeof window.nostojs !== "function") return;
      this._sdkHooked = true;
      window.nostojs(() => {
        setTimeout(() => {
          if (!window.nostoTemplatesConfig) {
            this._deadline = Math.min(this._deadline, Date.now());
          }
        }, TEMPLATES_GRACE);
      });
    }

    disconnectedCallback() {
      if (this._poll) {
        clearInterval(this._poll);
        this._poll = null;
      }
    }

    _tick(content) {
      this._hookSdk();
      if (content.firstElementChild) {
        clearInterval(this._poll);
        this._poll = null;
        const native = findNative(this);
        if (this._fellBack && native) {
          native.classList.remove("show-native");
        }
        // Do NOT reveal Nosto yet. `ax-nosto-loaded` starts the opacity
        // crossfade, and at this point #nosto-content holds only its first
        // child — the <nosto-dynamic-card>s are still fetching their markup.
        // Revealing now would fade the skeleton out over a half-empty grid and
        // the shopper would watch the cards/pagination pop in (the very CLS we
        // are removing). Instead we keep the (opaque) skeleton on top and let
        // collapseAfterSwap reveal + collapse together once the grid has
        // FILLED and its height is stable — a single clean swap.
        collapseAfterSwap([findSkeleton(this), native], content);
        this.dispatchEvent(
          new CustomEvent("nosto:ready", { bubbles: true, detail: { context: this.dataset.context } })
        );
        return;
      }
      if (!this._fellBack && Date.now() >= this._deadline) {
        this._fellBack = true;
        const native = findNative(this);
        if (native) native.classList.add("show-native");
        // content is empty (zero height) so only the skeleton needs collapsing
        collapseAfterSwap([findSkeleton(this)]);
        this.dispatchEvent(new CustomEvent("nosto:fallback", { bubbles: true }));
      }
      if (Date.now() - this._started > LATE_RECOVERY_WINDOW) {
        clearInterval(this._poll);
        this._poll = null;
      }
    }
  }

  customElements.define("nosto-react", NostoReact);

  /*
   * <nosto-reco-products placement="productpage-nosto-1" product-id="123"
   *                       limit="4" card-template="card" hidden>
   *
   * Bridge for Nosto recommendation campaigns rendered with the THEME's own
   * product cards (same approach as the Nosto PLP/search): the campaign is
   * fetched in JSON mode, each recommended product becomes a
   * <nosto-dynamic-card> (markup served by templates/product.{card-template}.liquid,
   * defaulting to product.card.liquid), appended into the
   * [data-nosto-reco-list] grid that the section provides.
   *
   * The element stays hidden until the campaign returns at least one
   * product, so a missing/empty campaign never leaves an empty section on
   * the page. Clicks are attributed to the campaign via the JS API.
   */
  function handleFromUrl(url) {
    try {
      const match = new URL(url, location.origin).pathname.match(/\/products\/([^/?#]+)/);
      return match ? match[1] : null;
    } catch (e) {
      return null;
    }
  }

  /*
   * Cross-placement de-duplication registry (page-scoped).
   *
   * Nosto's native "Filter duplicate products" only dedupes WITHIN a single
   * server-rendered campaign. Because each <nosto-reco-products> fetches its
   * campaign independently in JSON mode and stamps the theme's own cards, the
   * same product can surface in two placements on one page — e.g. the two
   * product-page recos "COMPLÉTER AVEC" (productpage-nosto-1) and "VOUS
   * AIMEREZ AUSSI" (productpage-nosto-2) — so a shopper sees the same item
   * twice even though "Filter duplicate products" is enabled in Nosto.
   *
   * Elements sharing a non-empty `dedupe-group` attribute pool their shown
   * handles here: a handle rendered by one is skipped by the others. Claiming
   * is serialised per group in element-CONNECTION order (DOM order = placement
   * priority) via a promise chain, so the FIRST placement keeps a shared
   * product and later placements drop it — deterministic regardless of which
   * campaign request resolves first. Only the fast synchronous claim/render is
   * ordered; the campaign fetches still run in parallel. Elements without a
   * group (the cart-drawer cross-sell) are unaffected. The registry lives for
   * the page lifetime; every product page is a full load, so it starts empty.
   */
  const dedupeRegistry = new Map(); // group -> { seen: Set<string>, tail: Promise }

  function dedupeGroupState(group) {
    let state = dedupeRegistry.get(group);
    if (!state) {
      state = { seen: new Set(), tail: Promise.resolve() };
      dedupeRegistry.set(group, state);
    }
    return state;
  }

  class NostoRecoProducts extends HTMLElement {
    connectedCallback() {
      if (this._started) return;
      this._started = true;
      this._setupDedupe();
      this._load();
    }

    // Join our dedupe group (if any) and reserve our slot in the group's claim
    // chain at CONNECTION time, so priority follows DOM/placement order rather
    // than the order campaign requests happen to resolve in.
    _setupDedupe() {
      const group = (this.getAttribute("dedupe-group") || "").trim();
      if (!group) return;
      const state = dedupeGroupState(group);
      this._dedupeSeen = state.seen;
      this._claimAfter = state.tail; // wait for all earlier placements to claim
      let release;
      state.tail = new Promise((resolve) => { release = resolve; }); // later ones wait for us
      this._releaseGate = release;
    }

    // Idempotent: lets the next placement in the group start claiming. Must run
    // on every _load() exit path so a failed/empty placement never blocks the
    // rest of the group.
    _releaseClaimGate() {
      const release = this._releaseGate;
      if (release) {
        this._releaseGate = null;
        release();
      }
    }

    // The nostojs stub is defined by Nosto's ASYNC app embed and may not
    // exist yet when this element connects — wait for it instead of bailing.
    _waitForNostojs() {
      return new Promise((resolve) => {
        const started = Date.now();
        const check = () => {
          if (typeof window.nostojs === "function") return resolve(window.nostojs);
          if (Date.now() - started > 10000) return resolve(null);
          setTimeout(check, 100);
        };
        check();
      });
    }

    async _load() {
      try {
        const placement = this.getAttribute("placement");
        const template = this.querySelector("template[data-nosto-reco-content]");
        if (!placement || !template) return;

        const productId = this.getAttribute("product-id");
        const limit = parseInt(this.getAttribute("limit"), 10) || 10;
        // Which Shopify card template <nosto-dynamic-card> fetches via
        // ?view={template}. Defaults to "card" (templates/product.card.liquid,
        // the vertical PLP card used by related-products); the cart drawer
        // cross-sell sets "cross-sell-card" for the horizontal compact card.
        const cardTemplate = this.getAttribute("card-template") || "card";

        const nostojs = await this._waitForNostojs();
        if (!nostojs) return this._markEmpty();

        const api = await new Promise(nostojs);
        const request = api
          .createRecommendationRequest({ includeTagging: true })
          .disableCampaignInjection()
          .setElements([placement])
          .setResponseMode("JSON_ORIGINAL");
        if (productId) {
          request.setProducts([{ product_id: productId }]);
        }
        const { recommendations } = await request.load();
        const campaign = recommendations && recommendations[placement];
        const candidates = campaign && campaign.products ? campaign.products : [];
        if (!candidates.length) return this._markEmpty();

        // Cross-placement de-duplication. Wait until every earlier placement in
        // this dedupe group has claimed its handles (connection order = DOM
        // order = placement priority), then drop any product already shown by
        // them — and any repeat within this same campaign — before capping to
        // `limit`. Filtering BEFORE the cap means a placement still fills up to
        // `limit` UNIQUE cards even when its top picks were taken by an earlier
        // placement, instead of showing fewer. Elements with no `dedupe-group`
        // (the cart-drawer cross-sell) have no `_claimAfter`/`_dedupeSeen` and
        // fall through unchanged.
        if (this._claimAfter) await this._claimAfter;
        const seen = this._dedupeSeen;
        const handles = [];
        const localHandles = new Set();
        for (const product of candidates) {
          const handle = handleFromUrl(product.url);
          if (!handle || localHandles.has(handle)) continue;
          if (seen && seen.has(handle)) continue;
          localHandles.add(handle);
          handles.push(handle);
          if (handles.length >= limit) break;
        }
        if (!handles.length) return this._markEmpty();
        // Claim now (synchronously) so later placements in the group skip these.
        if (seen) handles.forEach((handle) => seen.add(handle));

        // The section content is stamped only now: the theme's
        // <product-list> carousel must never be connected without cells.
        const content = template.content.cloneNode(true);
        const list = content.querySelector("[data-nosto-reco-list]");
        if (!list) return this._markEmpty();
        handles.forEach((handle) => {
          const card = document.createElement("nosto-dynamic-card");
          card.setAttribute("handle", handle);
          card.setAttribute("template", cardTemplate);
          list.appendChild(card);
        });
        if (!list.firstElementChild) return this._markEmpty();

        this.appendChild(content);
        api.attributeProductClicksInCampaign(this, campaign);
        this._reveal(list);
        this.dispatchEvent(
          new CustomEvent("nosto:reco-loaded", {
            bubbles: true,
            detail: { placement, count: list.childElementCount }
          })
        );
      } catch (e) {
        // Recommendations are an enhancement: any failure collapses the slot.
        this._markEmpty();
      } finally {
        // Always release our claim gate so later placements in the group are
        // never blocked — even if this one bailed early or threw.
        this._releaseClaimGate();
      }
    }

    // True when this instance reserves its slot with a skeleton (the related/
    // recently sections). The cart-drawer cross-sell has no skeleton and uses
    // the legacy `hidden` toggle instead — we keep both paths working.
    _hasSkeleton() {
      return !!this.querySelector("[data-nosto-reco-skeleton]");
    }

    // Campaign empty or failed: collapse the reserved slot (skeleton included)
    // so the page reads exactly as the old `hidden` behaviour did.
    _markEmpty() {
      this.classList.remove("nosto-reco--loading");
      this.classList.add("nosto-reco--empty");
      // Legacy collapse for cart-drawer / any markup still relying on [hidden].
      this.hidden = true;
    }

    // Reveal the real cards. For skeleton-backed sections we wait until the
    // cards have actually rendered, so the swap is iso-height (the skeleton
    // already reserved a row of card-sized boxes) — the <nosto-dynamic-card>s
    // fetch their markup async, exactly like the PLP, so we hold the opaque
    // skeleton on top until the grid fills (or a hard cap). For the cart-drawer
    // (no skeleton) we just unhide immediately, preserving the old behaviour.
    _reveal(list) {
      // Always unhide: cart-drawer relied on `hidden`, and it's harmless for
      // the skeleton sections (they're not [hidden], CSS drives their swap).
      this.hidden = false;

      if (!this._hasSkeleton()) {
        // Legacy path: nothing to crossfade, the content is shown as-is.
        return;
      }

      const expected = list.children.length;
      const started = Date.now();
      const MAX = 6000;
      let lastH = -1;
      let lastFilled = -1;
      let stable = 0;
      const filledCount = () => list.querySelectorAll("product-card, .product-card").length;
      const tick = () => {
        const h = Math.round(list.getBoundingClientRect().height);
        const filled = filledCount();
        if (h === lastH && filled === lastFilled) {
          stable += 1;
        } else {
          stable = 0;
          lastH = h;
          lastFilled = filled;
        }
        const done = filled >= expected && stable >= 3;
        if (done || Date.now() - started >= MAX) {
          this.classList.remove("nosto-reco--loading");
          this.classList.add("nosto-reco--loaded");
          return;
        }
        setTimeout(tick, 100);
      };
      tick();
    }
  }

  if (!customElements.get("nosto-reco-products")) {
    customElements.define("nosto-reco-products", NostoRecoProducts);
  }
})();
